import axios from 'axios';
import authService from './authService';

const API_URL = '/api/';

class AdminService {
  /**
   * Apartment Union Management
   */
  async getUnions() {
    const response = await axios.get(API_URL + 'unions/', {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async createUnion(data) {
    const response = await axios.post(API_URL + 'unions/', data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async updateUnion(id, data) {
    const response = await axios.put(API_URL + `unions/${id}/`, data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async deleteUnion(id) {
    const response = await axios.delete(API_URL + `unions/${id}/`, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  /**
   * Utility Type Management
   */
  async getUtilityTypes() {
    const response = await axios.get(API_URL + 'utility-types/', {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async createUtilityType(data) {
    const response = await axios.post(API_URL + 'utility-types/', data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async updateUtilityType(id, data) {
    const response = await axios.put(API_URL + `utility-types/${id}/`, data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async deleteUtilityType(id) {
    const response = await axios.delete(API_URL + `utility-types/${id}/`, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async importUtilityTypesFromBill(pdfFile) {
    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    const response = await axios.post(API_URL + 'utility-types/import-from-bill/', formData, {
      headers: {
        ...authService.authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    });
    return response.data;
  }

  /**
   * Union Membership Management
   */
  async getMemberships() {
    const response = await axios.get(API_URL + 'memberships/', {
      headers: authService.authHeader()
    });
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }

  async addMembership(data) {
    const response = await axios.post(API_URL + 'memberships/', data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async updateMembership(id, data) {
    const response = await axios.patch(API_URL + `memberships/${id}/`, data, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async deleteMembership(id) {
    await axios.delete(API_URL + `memberships/${id}/`, {
      headers: authService.authHeader()
    });
  }

  async searchUsers(q) {
    const response = await axios.get(API_URL + `memberships/search_users/?q=${encodeURIComponent(q)}`, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  /**
   * Address search (Estonian national registry)
   */
  async searchAddresses(query) {
    const encoded = encodeURIComponent(query);
    const response = await axios.get(API_URL + `addresses/search/${encoded}/`, {
      headers: authService.authHeader()
    });
    return response.data?.addresses || [];
  }

  /**
   * Auto-population from Estonian Registry
   */
  async populateUnionFromAddress(unionId, addressQuery) {
    const response = await axios.post(
      API_URL + `unions/${unionId}/populate-from-address/`,
      { address_query: addressQuery, confirmed: true },
      { headers: authService.authHeader() }
    );
    return response.data;
  }

  /**
   * Meter Readings
   */
  async getMeterReadings(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await axios.get(API_URL + `meter-readings/${query ? '?' + query : ''}`, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async importApartments(unionId, apartments) {
    const response = await axios.post(
      API_URL + `unions/${unionId}/import-apartments/`,
      { apartments },
      { headers: authService.authHeader() }
    );
    return response.data;
  }

  /**
   * Transactions
   */
  async getTransactions(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await axios.get(API_URL + `transactions/${query ? '?' + query : ''}`, {
      headers: authService.authHeader()
    });
    return response.data;
  }

  async importTransactionsCsv(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(API_URL + 'transactions/import-csv/', formData, {
      headers: {
        ...authService.authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async linkTransactionToInvoice(transactionId, invoiceId) {
    const response = await axios.patch(
      API_URL + `transactions/${transactionId}/`,
      { linked_invoice: invoiceId },
      { headers: authService.authHeader() }
    );
    return response.data;
  }
}

const adminService = new AdminService();
export default adminService;
